import React, { useState } from 'react'
import PropTypes from 'prop-types'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { Toast } from '@/shared/Toast'
import SVGIcon from '@/components/icon'

// This component is mounted as a standalone React root on project#show via
// `class: 'save-for-later-button'` (see entrypoints/application.jsx). It does
// NOT live inside the App.jsx tree, so the global <Toast /> rendered there
// isn't on the page. We render our own <Toast /> below the button so
// `setToast(...)` actually displays. `useGlobalState` is a singleton store
// (react-hooks-global-state), so the state is shared even though the
// renderer is local.
const SaveForLaterButton = ({ currentUserId, currentlyFavorited, projectId, roleId }) => {
  const [favorited, setFavorited] = useState(currentlyFavorited)
  const [, setToast] = useGlobalState('toast')
  const { putpostRequest } = useQuery()

  // The body states intent. Sending nothing left the server to flip whatever
  // it held, which meant a stale `currentlyFavorited` — rendered server-side at
  // page load, and stale the moment the user archived the row in another tab —
  // inverted the click. The response echoes the resulting state; adopt it.
  const toggleFavorite = () => {
    const previousState = favorited
    const next = !favorited
    setFavorited(next)

    const handleResponse = (err, jsonData) => {
      if (err) {
        setFavorited(previousState)
        const message = Array.isArray(err) ? err[0] : (typeof err === 'string' ? err : 'Could not save favorite. Please try again.')
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
          <p className="mt-1 text-sm dark:text-red-300 text-red-400">{message}</p>
        </div>)
        return
      }
      if (jsonData && typeof jsonData.favorited === 'boolean') { setFavorited(jsonData.favorited) }
    }

    if (projectId) {
      putpostRequest(`/api/v3/favorites/${projectId}/favorite_project`, 'POST', { favorited: next }, handleResponse)
    } else if (roleId) {
      putpostRequest(`/api/v3/favorites/${roleId}/favorite_role`, 'POST', { favorited: next }, handleResponse)
    }
  }

  return <>
    { favorited
      ? <button onClick={toggleFavorite} className="w-full h-full gap-x-1 flex items-center justify-center px-4 py-3 border text-sm text-cccblue font-medium rounded-md shadow-sm bg-cccpurple bg-opacity-20">
          <SVGIcon name='icon-favorited' className='h-6 w-6' />
          <span>Favorited!</span>
        </button>
      : <button onClick={toggleFavorite} className="w-full h-full gap-x-1 flex items-center justify-center px-4 py-3 border text-sm text-cccblue font-medium rounded-md shadow-sm bg-white dark:bg-gray-900">
          <SVGIcon name='icon-favorites' className='h-6 w-6' />
          <span>Favorite for Later</span>
        </button>
    }
    <Toast />
  </>
}

SaveForLaterButton.propTypes = {
  currentUserId: PropTypes.number.isRequired,
  currentlyFavorited: PropTypes.bool.isRequired,
  projectId: PropTypes.number,
  roleId: PropTypes.number
}

export default SaveForLaterButton
